import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from "react";
import Modal from "react-bootstrap/Modal";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { getValue } from "../Assets/Language/Entries";
import { getLang } from "../utils/common";
import APIFunctions from "../utils/APIFunctions";

/* ============================================================
   HELPERS
   ============================================================ */

const toCamelCase = (str) => (str ? str.charAt(0).toLowerCase() + str.slice(1) : str);
const normalizeKey = (k) => (k || "").toLowerCase().replace(/_/g, "");

const formatNumber = (value, col) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    if (isNaN(num)) return value;
    const decimals = col.decimalPlaces ?? 2;

    if (col.useScientificForSmall && num !== 0 && Math.abs(num) < 0.0001) {
        return num.toExponential(decimals);
    }
    if (!col.useThousandsSep) {
        return num.toFixed(decimals);
    }
    return num.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};

const formatCellValue = (value, col) => {
    if (value === null || value === undefined || value === "") return "";
    if (col.dataType === "number") return formatNumber(value, col);
    if (col.dataType === "bool") {
        if (value === true || value === "true" || value === 1) return "True";
        if (value === false || value === "false" || value === 0) return "False";
        return "Not specified";
    }
    return value;
};

const BooleanCell = ({ value }) => {
    if (value === null || value === undefined) {
        return <span title="Not specified" style={{ color: "#999" }}>—</span>;
    }
    const isTrue = value === true || value === "true" || value === 1;
    return (
        <span title={isTrue ? "True" : "False"} style={{ color: isTrue ? "#1e8e3e" : "#d93025", fontWeight: 700 }}>
            {isTrue ? "✓" : "✕"}
        </span>
    );
};

/* ============================================================
   LAYOUT CONSTANTS
   ============================================================ */

const ROW_HEIGHT = 34;
const HEADER_HEIGHT = 38;
const VIEWPORT_HEIGHT = 520;
const OVERSCAN = 10;
const ACTIONS_WIDTH = 150;

const COLORS = {
    text: "#1a1a1a",
    headerBg: "#eef4f6",
    headerText: "#0a3d4d",
    border: "#148ea1",
    rowBorder: "#e5e5e5",
    rowAlt: "#f7fafb",
    editBtnBg: "#e8f0ff",
    editBtnText: "#0a58ca",
    deleteBtnBg: "#fdeaea",
    deleteBtnText: "#c62828",
    primary: "#0a6e8f",
};

/* ============================================================
   VIRTUALIZED ROW (memoized)
   ============================================================ */

const GridRow = memo(function GridRow({ row, index, visibleColumns, getVal, isAdmin, onEdit, onDelete }) {
    const rowBg = index % 2 === 0 ? "#ffffff" : COLORS.rowAlt;

    return (
        <div
            style={{
                position: "absolute",
                top: index * ROW_HEIGHT,
                left: 0,
                display: "flex",
                width: "max-content",
                minWidth: "100%",
                height: ROW_HEIGHT,
                background: rowBg,
                borderBottom: `1px solid ${COLORS.rowBorder}`,
            }}
        >
            {visibleColumns.map((col) => {
                const cellValue = getVal(row, col.key);
                return (
                    <div
                        key={col.key}
                        title={String(formatCellValue(cellValue, col) ?? "")}
                        style={{
                            width: col.width,
                            minWidth: col.width,
                            flexShrink: 0,
                            padding: "6px 10px",
                            fontSize: "12px",
                            color: COLORS.text,
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            borderRight: `1px solid ${COLORS.rowBorder}`,
                        }}
                    >
                        {col.dataType === "bool" ? <BooleanCell value={cellValue} /> : formatCellValue(cellValue, col)}
                    </div>
                );
            })}

            {isAdmin && (
                <div
                    style={{
                        width: ACTIONS_WIDTH,
                        minWidth: ACTIONS_WIDTH,
                        flexShrink: 0,
                        padding: "3px 8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                    }}
                >
                    <button onClick={() => onEdit(row)} style={editBtnStyle}>
                        Edit
                    </button>
                    <button onClick={() => onDelete(row)} style={deleteBtnStyle}>
                        Delete
                    </button>
                </div>
            )}
        </div>
    );
});

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

const SpectrumAwardsExcelGrid = ({ isAdmin }) => {
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [footnotes, setFootnotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [userHiddenColumns, setUserHiddenColumns] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

    const [showColumnManager, setShowColumnManager] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [includeHiddenInExport, setIncludeHiddenInExport] = useState(false);

    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importPreview, setImportPreview] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = useRef(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editModalMode, setEditModalMode] = useState("edit");
    const [editModalData, setEditModalData] = useState({});
    const [editModalRow, setEditModalRow] = useState(null);
    const [isSavingModal, setIsSavingModal] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [rowPendingDelete, setRowPendingDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    /* ---------------- VIRTUALIZATION REFS/STATE ---------------- */
    const scrollContainerRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const rafRef = useRef(null);

    /* ---------------- LOAD DATA ---------------- */

    const loadColumns = useCallback(() => {
        return APIFunctions.getSpectrumAwardColumns().then((resp) => {
            const cfg = (resp?.data?.data || []).map((c) => ({
                key: c.dbColumnName,
                header: c.displayName,
                dataType: c.dataType,
                decimalPlaces: c.decimalPlaces,
                useThousandsSep: c.useThousandsSep,
                useScientificForSmall: c.useScientificForSmall,
                displayOrder: c.displayOrder,
                visibleDefault: c.isVisibleDefault,
                isEditable: c.isEditable,
                width: c.columnWidth || 150,
                columnConfigId: c.columnConfigId,
            }));
            cfg.sort((a, b) => a.displayOrder - b.displayOrder);
            setColumns(cfg);
        });
    }, []);

    const loadRows = useCallback(() => {
        return APIFunctions.getSpectrumAwards().then((resp) => {
            setRows(resp?.data?.data || []);
        });
    }, []);

    const loadFootnotes = useCallback(() => {
        return APIFunctions.getSpectrumAwardFootnotes().then((resp) => {
            setFootnotes(resp?.data?.data || []);
        });
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        Promise.all([loadColumns(), loadRows(), loadFootnotes()])
            .catch((err) => {
                console.error(err);
                setError("Failed to load spectrum awards data.");
            })
            .finally(() => setIsLoading(false));
    }, [loadColumns, loadRows, loadFootnotes]);

    /* ---------------- KEY RESOLUTION ---------------- */

    const dataKeyMap = useMemo(() => {
        const map = {};
        if (!rows.length || !columns.length) return map;
        const sampleKeys = Object.keys(rows[0]);
        const normalizedSample = sampleKeys.map((k) => ({ raw: k, norm: normalizeKey(k) }));
        columns.forEach((col) => {
            const target = normalizeKey(col.key);
            const found = normalizedSample.find((s) => s.norm === target);
            map[col.key] = found ? found.raw : toCamelCase(col.key);
        });
        return map;
    }, [rows, columns]);

    const getVal = useCallback(
        (row, colKey) => {
            const actualKey = dataKeyMap[colKey] || toCamelCase(colKey);
            return row ? row[actualKey] : undefined;
        },
        [dataKeyMap]
    );

    /* ---------------- VISIBLE COLUMNS ---------------- */

    const visibleColumns = useMemo(() => {
        return columns.filter((c) => {
            if (userHiddenColumns[c.key] !== undefined) return !userHiddenColumns[c.key];
            return c.visibleDefault;
        });
    }, [columns, userHiddenColumns]);

    const totalWidth = useMemo(() => {
        const sum = visibleColumns.reduce((acc, c) => acc + c.width, 0);
        return sum + (isAdmin ? ACTIONS_WIDTH : 0);
    }, [visibleColumns, isAdmin]);

    /* ---------------- SORTING ---------------- */

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return rows;
        const sorted = [...rows].sort((a, b) => {
            const va = getVal(a, sortConfig.key);
            const vb = getVal(b, sortConfig.key);
            if (va === null || va === undefined) return 1;
            if (vb === null || vb === undefined) return -1;
            if (typeof va === "number" && typeof vb === "number") return va - vb;
            return String(va).localeCompare(String(vb), undefined, { numeric: true });
        });
        return sortConfig.direction === "asc" ? sorted : sorted.reverse();
    }, [rows, sortConfig, getVal]);

    const handleSort = (columnKey) => {
        setSortConfig((prev) => {
            if (prev.key !== columnKey) return { key: columnKey, direction: "asc" };
            if (prev.direction === "asc") return { key: columnKey, direction: "desc" };
            return { key: null, direction: "asc" };
        });
    };

    /* ---------------- VIRTUALIZATION (callback ref — fixes mount-timing bug) ---------------- */

    const handleScroll = useCallback(() => {
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
            if (scrollContainerRef.current) {
                setScrollTop(scrollContainerRef.current.scrollTop);
            }
            rafRef.current = null;
        });
    }, []);

    // Callback ref: fires exactly when the DOM node mounts/unmounts.
    // This fixes the bug where the scroll listener was attached to `null`
    // because the old useEffect ran before the grid div existed (while the
    // loading screen was still showing), and never re-attached afterward.
    const setScrollRef = useCallback(
        (node) => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.removeEventListener("scroll", handleScroll);
            }
            scrollContainerRef.current = node;
            if (node) {
                node.addEventListener("scroll", handleScroll, { passive: true });
            }
        },
        [handleScroll]
    );

    useEffect(() => {
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    useEffect(() => {
        setScrollTop(0);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [sortedRows.length]);

    const totalRows = sortedRows.length;
    const totalContentHeight = totalRows * ROW_HEIGHT;
    const visibleRowCount = Math.ceil(VIEWPORT_HEIGHT / ROW_HEIGHT) + OVERSCAN * 2;
    const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const endIndex = Math.min(totalRows, startIndex + visibleRowCount);

    const visibleRows = [];
    for (let i = startIndex; i < endIndex; i++) {
        if (sortedRows[i]) {
            visibleRows.push({ row: sortedRows[i], index: i });
        }
    }

    /* ---------------- EDIT / ADD MODAL ---------------- */

    const openEditModal = useCallback(
        (row) => {
            const formData = {};
            columns.forEach((c) => {
                const actualKey = dataKeyMap[c.key] || toCamelCase(c.key);
                formData[actualKey] = row[actualKey] ?? "";
            });
            setEditModalData(formData);
            setEditModalRow(row);
            setEditModalMode("edit");
            setShowEditModal(true);
        },
        [columns, dataKeyMap]
    );

    const openAddModal = () => {
        const formData = {};
        columns.forEach((c) => {
            const actualKey = dataKeyMap[c.key] || toCamelCase(c.key);
            formData[actualKey] = "";
        });
        setEditModalData(formData);
        setEditModalRow(null);
        setEditModalMode("add");
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditModalData({});
        setEditModalRow(null);
    };

    const handleModalFieldChange = (actualKey, value) => {
        setEditModalData((prev) => ({ ...prev, [actualKey]: value }));
    };

   const parseFieldValue = (value, col) => {
    if (value === "" || value === null || value === undefined) return null;

    // TermYears must always remain a string for the C# backend
    if (col.key?.toLowerCase() === "termyears") {
        return String(value).trim();
    }

    if (col.dataType === "number") {
        const n = Number(String(value).replace(/,/g, ""));
        return isNaN(n) ? null : n;
    }
    if (col.dataType === "bool") {
        if (value === "true" || value === true) return true;
        if (value === "false" || value === false) return false;
        return null;
    }
    return String(value);
};

    const handleSaveModal = async () => {
    setIsSavingModal(true);
    try {
        const payload = { ...(editModalRow || {}) };
        columns.forEach((c) => {
            const actualKey = dataKeyMap[c.key] || toCamelCase(c.key);
            payload[actualKey] = parseFieldValue(editModalData[actualKey], c);
        });

        // Ensure termYears is never sent as a pure number
        if (payload.termYears !== null && payload.termYears !== undefined) {
            payload.termYears = String(payload.termYears);
        }

        if (editModalMode === "add") {
            const resp = await APIFunctions.createSpectrumAward(payload);
            if (resp?.data?.success) {
                setRows((prev) => [resp.data.data, ...prev]);
                closeEditModal();
            } else {
                alert(resp?.data?.message || "Failed to create record.");
            }
        } else {
            const resp = await APIFunctions.updateSpectrumAward(editModalRow.awardId, payload);
            if (resp?.data?.success) {
                const freshResp = await APIFunctions.getSpectrumAwardById(editModalRow.awardId);
                const freshRow = freshResp?.data?.data;
                setRows((prev) => prev.map((r) => (r.awardId === editModalRow.awardId ? freshRow : r)));
                closeEditModal();
            } else {
                alert(resp?.data?.message || "Update failed. The row may have been changed by another user — refreshing.");
                await loadRows();
                closeEditModal();
            }
        }
    } catch (err) {
        console.error(err);
        const serverError = err?.response?.data?.message || "An error occurred while saving. Please try again.";
        alert(serverError);
    } finally {
        setIsSavingModal(false);
    }
};

    /* ---------------- DELETE CONFIRMATION MODAL ---------------- */

    const openDeleteModal = useCallback((row) => {
        setRowPendingDelete(row);
        setShowDeleteModal(true);
    }, []);

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setRowPendingDelete(null);
    };

    const confirmDelete = async () => {
        if (!rowPendingDelete) return;
        setIsDeleting(true);
        try {
            const resp = await APIFunctions.deleteSpectrumAward(rowPendingDelete.awardId);
            if (resp?.data?.success) {
                setRows((prev) => prev.filter((r) => r.awardId !== rowPendingDelete.awardId));
                closeDeleteModal();
            } else {
                alert(resp?.data?.message || "Delete failed.");
            }
        } catch (err) {
            console.error(err);
            alert("An error occurred while deleting.");
        } finally {
            setIsDeleting(false);
        }
    };

    /* ---------------- COLUMN MANAGER ---------------- */

    const toggleUserColumnVisibility = (key) => {
        setUserHiddenColumns((prev) => {
            const currentlyVisible = prev[key] !== undefined ? !prev[key] : columns.find((c) => c.key === key)?.visibleDefault;
            return { ...prev, [key]: currentlyVisible };
        });
    };

    const toggleAdminDefaultVisibility = async (col) => {
        if (!isAdmin) return;
        try {
            await APIFunctions.updateColumnConfig(col.columnConfigId, {
                displayName: col.header,
                decimalPlaces: col.decimalPlaces,
                useThousandsSep: col.useThousandsSep,
                useScientificForSmall: col.useScientificForSmall,
                displayOrder: col.displayOrder,
                isVisibleDefault: !col.visibleDefault,
                isEditable: col.isEditable,
                columnWidth: col.width,
            });
            await loadColumns();
        } catch (err) {
            console.error(err);
            alert("Failed to update column default visibility.");
        }
    };

    /* ---------------- EXPORT ---------------- */

    const handleExport = () => {
        const exportCols = includeHiddenInExport ? columns : visibleColumns;
        const result = sortedRows.map((row) => {
            const item = {};
            exportCols.forEach((col) => {
                item[col.header] = formatCellValue(getVal(row, col.key), col);
            });
            return item;
        });

        const ws = XLSX.utils.json_to_sheet(result);
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, "SpectrumAwards.xlsx");
        setShowExportModal(false);
    };

    /* ---------------- DOWNLOAD TEMPLATE (new) ----------------
       Generates a blank .xlsx with just the header row, using the exact
       DisplayName values from AwardsColumnConfig in the correct order —
       so admin fills in data using headers the import parser recognizes,
       avoiding "column not matched" errors during import.
    ------------------------------------------------------------- */

    const handleDownloadTemplate = () => {
        const headers = columns
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((c) => c.header);

        // One empty row so Excel shows proper column formatting when opened,
        // without providing any fake/misleading sample data.
        const emptyRow = {};
        headers.forEach((h) => (emptyRow[h] = ""));

        const ws = XLSX.utils.json_to_sheet([emptyRow], { header: headers });
        const wb = { Sheets: { Template: ws }, SheetNames: ["Template"] };
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(data, "SpectrumAwards_ImportTemplate.xlsx");
    };

    /* ---------------- IMPORT ---------------- */

    const handleFileSelected = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImportFile(file);
            setImportPreview(null);
        }
    };

    const handlePreviewImport = async () => {
        if (!importFile) return;
        setIsImporting(true);
        try {
            const formData = new FormData();
            formData.append("file", importFile);
            const resp = await APIFunctions.importSpectrumAwardPreview(formData);
            if (resp?.data?.success) {
                setImportPreview(resp.data.data);
            } else {
                alert(resp?.data?.message || "Preview failed.");
            }
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.message || "An error occurred while previewing the file.");
        } finally {
            setIsImporting(false);
        }
    };

    const handleCommitImport = async () => {
        if (!importPreview?.importToken) return;
        setIsImporting(true);
        try {
            const resp = await APIFunctions.importSpectrumAwardCommit({
                importToken: importPreview.importToken,
                fileName: importFile?.name,
            });
            if (resp?.data?.success) {
                alert(`Import successful: ${resp.data.data.rowsImported} rows added.`);
                setShowImportModal(false);
                setImportFile(null);
                setImportPreview(null);
                await loadRows();
            } else {
                alert(resp?.data?.message || "Import failed.");
            }
        } catch (err) {
            console.error(err);
            alert("An error occurred while committing the import.");
        } finally {
            setIsImporting(false);
        }
    };

    const closeImportModal = () => {
        setShowImportModal(false);
        setImportFile(null);
        setImportPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    /* ---------------- RENDER ---------------- */

    if (isLoading) {
        return (
            <div style={{ textAlign: "center", padding: "40px" }}>
                <div className="ssl-spinner" style={{ margin: "0 auto 12px" }} />
                <span>Loading spectrum awards...</span>
            </div>
        );
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div style={{ paddingTop: "45px", color: COLORS.text }}>
            {/* ---------------- TOOLBAR ---------------- */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px", marginBottom: "8px" }}>
                <h4 style={{ margin: 0, color: COLORS.primary, fontWeight: 700 }}>
                    {getValue("SpectrumAwards", getLang()) || "Spectrum Awards"}
                </h4>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <button style={btnSecondaryStyle} onClick={() => setShowColumnManager(true)}>
                        Columns
                    </button>
                    <button style={{ ...btnPrimaryStyle, opacity: rows.length === 0 ? 0.5 : 1 }} onClick={() => setShowExportModal(true)} disabled={rows.length === 0}>
                        Export
                    </button>
                    {isAdmin && (
                        <>
                            <button style={btnSuccessStyle} onClick={openAddModal}>
                                + Add Row
                            </button>
                            <button style={btnInfoStyle} onClick={() => setShowImportModal(true)}>
                                Import Excel
                            </button>
                        </>
                    )}
                </div>
            </div>

            <h6 style={{ fontWeight: 700, fontSize: "14px", color: "#333", marginBottom: "8px" }}>
                {getValue("NumberOfAwards", getLang()) || "Number Of Awards"}: {rows.length}
            </h6>

            {/* ---------------- GRID ---------------- */}
            <div
                ref={setScrollRef}
                style={{
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: "6px",
                    overflow: "auto",
                    height: VIEWPORT_HEIGHT + HEADER_HEIGHT,
                    position: "relative",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        width: totalWidth,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                        background: COLORS.headerBg,
                        borderBottom: `2px solid ${COLORS.border}`,
                        height: HEADER_HEIGHT,
                    }}
                >
                    {visibleColumns.map((col) => (
                        <div
                            key={col.key}
                            onClick={() => handleSort(col.key)}
                            title={col.header}
                            style={{
                                width: col.width,
                                minWidth: col.width,
                                flexShrink: 0,
                                padding: "8px 10px",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: COLORS.headerText,
                                cursor: "pointer",
                                userSelect: "none",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                borderRight: "1px solid #d5dfe1",
                            }}
                        >
                            {col.header}
                            {sortConfig.key === col.key && (
                                <span style={{ marginLeft: 5 }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
                            )}
                        </div>
                    ))}
                    {isAdmin && (
                        <div style={{ width: ACTIONS_WIDTH, minWidth: ACTIONS_WIDTH, flexShrink: 0, padding: "8px 10px", fontWeight: 700, color: COLORS.headerText }}>
                            Actions
                        </div>
                    )}
                </div>

                <div style={{ position: "relative", width: totalWidth, height: totalContentHeight }}>
                    {visibleRows.map(({ row, index }) => (
                        <GridRow
                            key={row.awardId}
                            row={row}
                            index={index}
                            visibleColumns={visibleColumns}
                            getVal={getVal}
                            isAdmin={isAdmin}
                            onEdit={openEditModal}
                            onDelete={openDeleteModal}
                        />
                    ))}
                </div>
            </div>

            {footnotes.length > 0 && (
                <div style={{ marginTop: "12px", fontSize: "11px", color: "#555" }}>
                    {footnotes.map((f) => (
                        <div key={f.footnoteId}>* {f.footnoteText}</div>
                    ))}
                </div>
            )}

            {/* ---------------- ADD / EDIT MODAL ---------------- */}
            <Modal show={showEditModal} onHide={closeEditModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editModalMode === "add" ? "Add Spectrum Award" : "Edit Spectrum Award"}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "65vh", overflowY: "auto", color: COLORS.text }}>
                    <div className="row">
                        {columns.map((col) => {
                            const actualKey = dataKeyMap[col.key] || toCamelCase(col.key);
                            const value = editModalData[actualKey] ?? "";
                            return (
                                <div className="col-md-6 mb-3" key={col.key}>
                                    <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px" }}>
                                        {col.header}
                                    </label>
                                    {col.dataType === "bool" ? (
                                        <select
                                            className="form-control form-control-sm"
                                            value={value === true ? "true" : value === false ? "false" : ""}
                                            onChange={(e) => handleModalFieldChange(actualKey, e.target.value)}
                                        >
                                            <option value="">—</option>
                                            <option value="true">True</option>
                                            <option value="false">False</option>
                                        </select>
                                    ) : (
                                        <input
                                            className="form-control form-control-sm"
                                            value={value}
                                            onChange={(e) => handleModalFieldChange(actualKey, e.target.value)}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={closeEditModal} disabled={isSavingModal}>
                        Cancel
                    </button>
                    <button style={btnPrimaryStyle} onClick={handleSaveModal} disabled={isSavingModal}>
                        {isSavingModal ? "Saving..." : "Save"}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ---------------- DELETE CONFIRMATION MODAL ---------------- */}
            <Modal show={showDeleteModal} onHide={closeDeleteModal} size="sm" centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: "16px" }}>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ color: COLORS.text }}>
                    {rowPendingDelete && (
                        <p style={{ margin: 0 }}>
                            Are you sure you want to delete this record for{" "}
                            <strong>{getVal(rowPendingDelete, "Country")}</strong> (
                            {getVal(rowPendingDelete, "AwardYear")}, {getVal(rowPendingDelete, "Operator")})? This action cannot be undone from this screen.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={closeDeleteModal} disabled={isDeleting}>
                        Cancel
                    </button>
                    <button className="btn btn-danger" onClick={confirmDelete} disabled={isDeleting}>
                        {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                </Modal.Footer>
            </Modal>

            {/* ---------------- COLUMN MANAGER MODAL ---------------- */}
            <Modal show={showColumnManager} onHide={() => setShowColumnManager(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Manage Columns</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto", color: COLORS.text }}>
                    {!isAdmin && (
                        <p className="text-muted" style={{ fontSize: "12px" }}>
                            Changes here only affect your own view and are not saved.
                        </p>
                    )}
                    {columns.map((col) => {
                        const isVisible = userHiddenColumns[col.key] !== undefined ? !userHiddenColumns[col.key] : col.visibleDefault;
                        return (
                            <div key={col.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #eee" }}>
                                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", margin: 0 }}>
                                    <input type="checkbox" checked={isVisible} onChange={() => toggleUserColumnVisibility(col.key)} />
                                    {col.header}
                                </label>
                                {isAdmin && (
                                    <button
                                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: "11px" }}
                                        title="Set as default for all users"
                                        onClick={() => toggleAdminDefaultVisibility(col)}
                                    >
                                        {col.visibleDefault ? (
                                            <span style={{ color: "#1e8e3e" }}>Default: Shown</span>
                                        ) : (
                                            <span style={{ color: "#999" }}>Default: Hidden</span>
                                        )}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </Modal.Body>
            </Modal>

            {/* ---------------- EXPORT MODAL ---------------- */}
            <Modal show={showExportModal} onHide={() => setShowExportModal(false)} size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Export</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ color: COLORS.text }}>
                    <div style={{ marginBottom: "16px" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                            <input
                                type="checkbox"
                                checked={includeHiddenInExport}
                                onChange={(e) => setIncludeHiddenInExport(e.target.checked)}
                            />
                            Include hidden columns
                        </label>
                    </div>
                    <button style={{ ...btnPrimaryStyle, width: "100%" }} onClick={handleExport}>
                        Export to Excel
                    </button>
                </Modal.Body>
            </Modal>

            {/* ---------------- IMPORT MODAL ---------------- */}
            {isAdmin && (
                <Modal show={showImportModal} onHide={closeImportModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Import Excel</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ color: COLORS.text }}>
                        {!importPreview && (
                            <>
                                <div
                                    style={{
                                        background: "#f0f7f9",
                                        border: "1px solid rgba(10,110,143,0.25)",
                                        borderRadius: "6px",
                                        padding: "12px 14px",
                                        marginBottom: "16px",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: "8px",
                                    }}
                                >
                                    <div style={{ fontSize: "12px", color: "#333" }}>
                                        New to importing? Download a blank template with the correct column headers first.
                                    </div>
                                    <button style={btnSecondaryStyle} onClick={handleDownloadTemplate}>
                                        Download Template
                                    </button>
                                </div>

                                <input type="file" accept=".xlsx" ref={fileInputRef} onChange={handleFileSelected} className="form-control mb-3" />
                                <button style={btnSecondaryStyle} onClick={handlePreviewImport} disabled={!importFile || isImporting}>
                                    {isImporting ? "Analyzing..." : "Preview Import"}
                                </button>
                            </>
                        )}

                        {importPreview && (
                            <div>
                                <div className="row mb-3">
                                    <div className="col-3"><strong>{importPreview.totalRowsInFile}</strong><div style={{ fontSize: "11px" }}>Total rows</div></div>
                                    <div className="col-3"><strong style={{ color: "#1e8e3e" }}>{importPreview.newRowsCount}</strong><div style={{ fontSize: "11px" }}>New rows</div></div>
                                    <div className="col-3"><strong style={{ color: "#999" }}>{importPreview.duplicateRowsCount}</strong><div style={{ fontSize: "11px" }}>Duplicates</div></div>
                                    <div className="col-3"><strong style={{ color: "#d93025" }}>{importPreview.invalidRowsCount}</strong><div style={{ fontSize: "11px" }}>Invalid</div></div>
                                </div>

                                {importPreview.errors?.length > 0 && (
                                    <div className="alert alert-warning" style={{ fontSize: "12px", maxHeight: "150px", overflowY: "auto" }}>
                                        {importPreview.errors.map((e, i) => (
                                            <div key={i}>Row {e.rowNumber} [{e.columnName}]: {e.errorMessage}</div>
                                        ))}
                                    </div>
                                )}

                                <div className="d-flex justify-content-end gap-2 mt-3">
                                    <button className="btn btn-secondary" onClick={closeImportModal}>Cancel</button>
                                    <button className="btn btn-success" onClick={handleCommitImport} disabled={isImporting || importPreview.newRowsCount === 0}>
                                        {isImporting ? "Importing..." : `Confirm Import (${importPreview.newRowsCount} rows)`}
                                    </button>
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};

/* ============================================================
   INLINE STYLES
   ============================================================ */

const btnBase = {
    fontSize: "13px",
    padding: "6px 14px",
    borderRadius: "4px",
    cursor: "pointer",
};

const btnPrimaryStyle = { ...btnBase, background: COLORS.primary, color: "#fff", border: "none" };
const btnSecondaryStyle = { ...btnBase, background: "#e8f4f7", color: COLORS.primary, border: `1px solid ${COLORS.primary}` };
const btnSuccessStyle = { ...btnBase, background: "#1e8e3e", color: "#fff", border: "none" };
const btnInfoStyle = { ...btnBase, background: "#0a58ca", color: "#fff", border: "none" };

const editBtnStyle = {
    background: COLORS.editBtnBg,
    color: COLORS.editBtnText,
    border: `1px solid ${COLORS.editBtnText}`,
    borderRadius: "4px",
    padding: "3px 10px",
    fontSize: "11px",
    fontWeight: 600,
    cursor: "pointer",
};

const deleteBtnStyle = {
    background: COLORS.deleteBtnBg,
    color: COLORS.deleteBtnText,
    border: `1px solid ${COLORS.deleteBtnText}`,
    borderRadius: "4px",
    padding: "3px 10px",
    fontSize: "11px",
    fontWeight: 600,
    cursor: "pointer",
};

export default SpectrumAwardsExcelGrid;
